import PropTypes from 'prop-types';
import { date, duration as durationUtil, string } from '@eti/utils';
import { CarrierLogo, Icon } from '@eti/components';
import { css, etiColors } from '@eti/styles';
import { useDateFormat, useSiteContext, useTranslation } from '@eti/providers';
import { Text } from '@etg/wings';
import { getBoundTitle } from '../../../trip-details/utils/format';

const boundInfoStyles = css`
  display: flex;
  position: relative;
`;

const logoHolderStyles = css`
  display: flex;
  position: absolute;
  right: 0;
  top: 0;

  [dir='rtl'] & {
    left: 0;
    right: auto;
  }
`;

const stopsStyles = css`
  display: flex;
  margin: 0 5px;
`;

const stopsIconWrapperStyles = css`
  height: 0.8rem;
  margin: 0 5px;
  width: 0.8rem;
`;

const carrierLogoInfoStyles = css`
  height: 20px;
  margin: 0 4px;
  width: 20px;
`;

const titleStyles = css`
  margin-right: 5px;
`;

const CartTripBoundInfo = ({ boundId, isMultiStop, marketingCarriers, segment, type }) => {
  const { t } = useTranslation();
  const { language, timeFormat } = useSiteContext();
  const { shortWeekdayYear, time } = useDateFormat();
  const title = t(getBoundTitle(type, isMultiStop), parseInt(boundId, 10) + 1);

  const { arrivedAt, duration, departuredAt, destination, origin, stops } = segment;
  return (
    <>
      <Text
        as="p"
        className={titleStyles}
        data-testid="cart-trip-bound-info"
        variant="18-emphasized"
      >
        {title}
      </Text>
      <div className={boundInfoStyles} data-testid={`cart-trip-bound-info-date-${boundId}`}>
        <Text>{string.firstToUpperCase(date.format(departuredAt, shortWeekdayYear))}</Text>
        {marketingCarriers && (
          <div className={logoHolderStyles}>
            {marketingCarriers.map(({ code, name }) => (
              <CarrierLogo
                className={carrierLogoInfoStyles}
                data-testid={`cart-trip-bound-info-carrierLogo-${code}`}
                id={code || ''}
                imageAlt={name}
                key={`marketing-carrier-${code}`}
              />
            ))}
          </div>
        )}
      </div>
      <div className={boundInfoStyles} data-testid={`cart-trip-bound-info-time-${boundId}`}>
        <Text as="p" className={titleStyles} variant="16-bold">
          {date.format(departuredAt, time)} - {date.format(arrivedAt, time)}
        </Text>
        {`  (${durationUtil.getDuration(duration, language.code, timeFormat)})`}
        {stops > 0 && (
          <div className={stopsStyles} data-testid={`cart-trip-bound-info-stops-${boundId}`}>
            <div className={stopsIconWrapperStyles}>
              <Icon
                aria-label={t('Air.Filter.Group.Stops')}
                color={etiColors.black}
                id="plane-arrive"
                isGraphic
                size="0.8rem"
              />
            </div>
            <Text>{stops}</Text>
          </div>
        )}
      </div>
      <div className={boundInfoStyles} data-testid={`cart-trip-bound-info-location-${boundId}`}>
        <Text>
          {origin.code} {origin.cityName} - {destination.code} {destination.cityName}
        </Text>
      </div>
    </>
  );
};

CartTripBoundInfo.propTypes = {
  boundId: PropTypes.string,
  isMultiStop: PropTypes.bool,
  marketingCarriers: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  segment: PropTypes.object,
  type: PropTypes.string,
};

export default CartTripBoundInfo;
